import {
  AssignmentModel,
  AssignmentStatusEnum,
  AssignmentTypeEnum,
  AssignmentViewModel,
  AvailabilityDto,
  CaseExtendedModel,
  CaseModel,
  CaseStatusEnum,
  CaseTypeEnum,
} from 'api/model';
import ContextWrappedCreateCaseModal from 'components/CreateCaseModal';
import useModalStack from 'contexts/modal/useModalStack';
// import { IAddress } from 'utils/address';
import { getLastAssignmentDetails } from './worktime-helper';
import { DayDrivingTime } from './useDayDrivingTimes';

export enum PlaceholderAssignmentTypeEnum {
  PersonalTransport,
  CarPlaceholder,
}

type Props = {
  bookedAssignments?: AssignmentViewModel[];
  drivingTimes?: DayDrivingTime[];

  availability: AvailabilityDto;
  userId: number;
};

export default function useCreatePlaceholderAssignments({
  bookedAssignments = [],
  drivingTimes = [],
  availability,
}: Props) {
  const modalStack = useModalStack();

  const handleCreateMorningPlaceholder = (
    type: PlaceholderAssignmentTypeEnum
  ) => {
    if (bookedAssignments.length === 0) {
      return;
    }

    // set start time to 08:00
    const placeholderStartTime = new Date(availability.dateString);
    placeholderStartTime.setHours(8);
    placeholderStartTime.setMinutes(0);
    placeholderStartTime.setSeconds(0);
    placeholderStartTime.setMilliseconds(0);
    // set estimated duration (ms) to the amount of time between 08:00 and the first assignment's estimated start time
    const firstDrivingTime = drivingTimes[0];
    const firstBooking = bookedAssignments[0];
    const nextAssignmentStartTime = firstDrivingTime.start;
    const placeholderEstimatedDuration =
      nextAssignmentStartTime.getTime() - placeholderStartTime.getTime();
    // set to address details to the first assignment's from address details
    const placeHolderToAddress = firstDrivingTime.fromAddress;
    const assignedToUser = firstBooking?.assignment.assignedTo;

    let defaults: CaseExtendedModel;
    if (type === PlaceholderAssignmentTypeEnum.PersonalTransport) {
      defaults = {
        // create a new assignment inside the existing case
        case: {
          ...bookedAssignments[0].case,
          assignmentList: [
            ...bookedAssignments[0].case.assignmentList,
            {
              assignmentID: -1,
              timestamp: '',
              assignmentStatusID: AssignmentStatusEnum.Planned,
              assignmentTypeID: AssignmentTypeEnum.GoByTrain,
              assignedTo: assignedToUser,
              bookedTo: placeholderStartTime,
              estimatedStartTime: placeholderStartTime,
              estimatedDuration: placeholderEstimatedDuration,
              fromAddress: '',
              fromCity: '',
              fromZip: '',
              toName: placeHolderToAddress.name,
              toAddress: placeHolderToAddress.address,
              toCity: placeHolderToAddress.city,
              toZip: placeHolderToAddress.zip,
              toCounty: placeHolderToAddress.county,
            } as AssignmentModel,
          ],
        },
      } as CaseExtendedModel;
    } else {
      defaults = {
        // create new case with placeholder assignment
        case: {
          caseID: -1,
          caseStatusID: CaseStatusEnum.Open,
          caseTypeID: CaseTypeEnum.Placeholder,
          vehicleMileage: 0,
          assignmentList: [
            {
              assignmentID: -1,
              timestamp: '',
              assignmentStatusID: AssignmentStatusEnum.Planned,
              assignmentTypeID: AssignmentTypeEnum.CarPlaceholder,
              assignedTo: assignedToUser,
              bookedTo: placeholderStartTime,
              estimatedStartTime: placeholderStartTime,
              estimatedDuration: placeholderEstimatedDuration,
              fromAddress: '',
              fromCity: '',
              fromZip: '',
              toName: placeHolderToAddress.name,
              toAddress: placeHolderToAddress.address,
              toCity: placeHolderToAddress.city,
              toZip: placeHolderToAddress.zip,
              toCounty: placeHolderToAddress.county,
            } as AssignmentModel,
          ],
        } as CaseModel,
      } as CaseExtendedModel;
    }

    modalStack.push(
      <ContextWrappedCreateCaseModal
        goToCreated={false}
        onClose={() => {
          modalStack.pop();
        }}
        onCaseCreated={() => {
          modalStack.pop();
        }}
        newAssignmentOverrides={{
          lastPossibleEndTime: nextAssignmentStartTime,
          isUsersFirstAssignmentOfDay: true,
        }}
        defaults={defaults}
        caseType={defaults.case.caseTypeID}
      />
    );
  };

  const handleCreateAfternoonPlaceholder = (
    type: PlaceholderAssignmentTypeEnum
  ) => {
    const lastAssignmentDetails = getLastAssignmentDetails(bookedAssignments);
    const lastEntryOfDay = drivingTimes[drivingTimes.length - 1];
    const lastEntryRequest = lastEntryOfDay?.assignmentRequest;

    // En annonsförfrågan som avslutar dagen finns inte i bookedAssignments —
    // ankra då utfyllnaden på förfrågans destination och härledda sluttid.
    const anchor = lastEntryRequest
      ? {
          placeholderStartTime: lastEntryOfDay.end,
          placeHolderToAddress: lastEntryOfDay.toAddress,
          assignedToUser: lastEntryRequest.requestedFor,
        }
      : lastAssignmentDetails;

    if (!anchor) {
      return;
    }

    const { placeholderStartTime, placeHolderToAddress, assignedToUser } =
      anchor;
    let defaults: CaseExtendedModel;
    if (type === PlaceholderAssignmentTypeEnum.PersonalTransport) {
      // Kollektivtrafik läggs som nytt uppdrag i ett befintligt ärende; en
      // annonsförfrågan har inget ärende, så använd dagens sista bokade uppdrag.
      const caseToAppendTo = lastAssignmentDetails?.assignment.case;
      if (!caseToAppendTo) {
        return;
      }
      defaults = {
        case: {
          ...caseToAppendTo,
          assignmentList: [
            ...caseToAppendTo.assignmentList,
            {
              assignmentID: -1,
              timestamp: '',
              assignmentStatusID: AssignmentStatusEnum.Planned,
              assignmentTypeID: AssignmentTypeEnum.GoByTrain,
              assignedTo: assignedToUser,
              bookedTo: placeholderStartTime,
              estimatedStartTime: placeholderStartTime,
              // estimatedDuration: placeholderEstimatedDuration,
              fromName: placeHolderToAddress.name,
              fromAddress: placeHolderToAddress.address,
              fromCity: placeHolderToAddress.city,
              fromZip: placeHolderToAddress.zip,
              fromCounty: placeHolderToAddress.county,
              toName: '',
              toAddress: '',
              toCity: '',
              toZip: '',
            } as AssignmentModel,
          ],
        } as CaseModel,
      } as CaseExtendedModel;
    } else {
      defaults = {
        case: {
          caseID: -1,
          caseStatusID: CaseStatusEnum.Open,
          caseTypeID: CaseTypeEnum.Placeholder,
          vehicleMileage: 0,
          assignmentList: [
            {
              assignmentID: -1,
              timestamp: '',
              assignmentStatusID: AssignmentStatusEnum.Planned,
              assignmentTypeID: AssignmentTypeEnum.Normal,
              assignedTo: assignedToUser,
              bookedTo: placeholderStartTime,
              estimatedStartTime: placeholderStartTime,
              // estimatedDuration: placeholderEstimatedDuration,
              fromName: placeHolderToAddress.name,
              fromAddress: placeHolderToAddress.address,
              fromCity: placeHolderToAddress.city,
              fromZip: placeHolderToAddress.zip,
              fromCounty: placeHolderToAddress.county,
              toName: '',
              toAddress: '',
              toCity: '',
              toZip: '',
            } as AssignmentModel,
          ],
        } as CaseModel,
      } as CaseExtendedModel;
    }

    modalStack.push(
      <ContextWrappedCreateCaseModal
        goToCreated={false}
        onClose={() => {
          modalStack.pop();
        }}
        onCaseCreated={() => {
          modalStack.pop();
        }}
        newAssignmentOverrides={{
          firstPossibleStartTime: placeholderStartTime,
          isUsersLastAssignmentOfDay: true,
        }}
        defaults={defaults}
        caseType={defaults.case.caseTypeID}
      />
    );
  };

  const handleMidAssignmentPlaceholder = (
    fromAssignment: DayDrivingTime,
    toAssignment: DayDrivingTime,
    type: PlaceholderAssignmentTypeEnum
  ) => {
    const placeholderStartTime = fromAssignment.end;

    const placeholderEndTime = toAssignment.start;

    const placeholderEstimatedDuration =
      placeholderEndTime.getTime() - placeholderStartTime.getTime();

    const assignedToUser =
      toAssignment.assignment?.assignment.assignedTo ||
      toAssignment.assignmentRequest?.requestedFor;

    let defaults: CaseExtendedModel | undefined;
    if (type === PlaceholderAssignmentTypeEnum.PersonalTransport) {
      const toAssignmentCase = toAssignment.assignment;
      const fromAssignmentCase = fromAssignment.assignment;

      // Append defaults for the case we have case info for to apply (-> so no AssignmentRequest to AssignmentRequest handling here)
      // Prioritizing the toAssignment's case info if both are available (since that is the previous default behavior)
      if (toAssignmentCase) {
        defaults = {
          case: {
            ...toAssignmentCase.case,
            assignmentList: [
              ...toAssignmentCase.case.assignmentList,
              {
                assignmentID: -1,
                timestamp: '',
                assignmentStatusID: AssignmentStatusEnum.Planned,
                assignmentTypeID: AssignmentTypeEnum.GoByTrain,
                assignedTo: assignedToUser,
                bookedTo: placeholderStartTime,
                estimatedStartTime: placeholderStartTime,
                estimatedDuration: placeholderEstimatedDuration,
                fromName: fromAssignment.toAddress.name,
                fromAddress: fromAssignment.toAddress.address,
                fromCity: fromAssignment.toAddress.city,
                fromZip: fromAssignment.toAddress.zip,
                fromCounty: fromAssignment.toAddress.county,
                toName: toAssignment.fromAddress.name,
                toAddress: toAssignment.fromAddress.address,
                toCity: toAssignment.fromAddress.city,
                toZip: toAssignment.fromAddress.zip,
                toCounty: toAssignment.fromAddress.county,
                breakTime: 0,
              } as AssignmentModel,
            ],
          },
        } as CaseExtendedModel;
      } else if (fromAssignmentCase) {
        defaults = {
          case: {
            ...fromAssignmentCase.case,
            assignmentList: [
              ...fromAssignmentCase.case.assignmentList,
              {
                assignmentID: -1,
                timestamp: '',
                assignmentStatusID: AssignmentStatusEnum.Planned,
                assignmentTypeID: AssignmentTypeEnum.GoByTrain,
                assignedTo: assignedToUser,
                bookedTo: placeholderStartTime,
                estimatedStartTime: placeholderStartTime,
                estimatedDuration: placeholderEstimatedDuration,
                fromName: fromAssignment.toAddress.name,
                fromAddress: fromAssignment.toAddress.address,
                fromCity: fromAssignment.toAddress.city,
                fromZip: fromAssignment.toAddress.zip,
                fromCounty: fromAssignment.toAddress.county,
                toName: toAssignment.fromAddress.name,
                toAddress: toAssignment.fromAddress.address,
                toCity: toAssignment.fromAddress.city,
                toZip: toAssignment.fromAddress.zip,
                toCounty: toAssignment.fromAddress.county,
                breakTime: 0,
              } as AssignmentModel,
            ],
          },
        } as CaseExtendedModel;
      }
    } else {
      defaults = {
        case: {
          caseID: -1,
          caseStatusID: CaseStatusEnum.Open,
          caseTypeID: CaseTypeEnum.Placeholder,
          vehicleMileage: 0,
          assignmentList: [
            {
              assignmentID: -1,
              timestamp: '',
              assignmentStatusID: AssignmentStatusEnum.Planned,
              assignmentTypeID: AssignmentTypeEnum.Normal,
              assignedTo: assignedToUser,
              bookedTo: placeholderStartTime,
              estimatedStartTime: placeholderStartTime,
              estimatedDuration: placeholderEstimatedDuration,
              fromName: fromAssignment.toAddress.name,
              fromAddress: fromAssignment.toAddress.address,
              fromCity: fromAssignment.toAddress.city,
              fromZip: fromAssignment.toAddress.zip,
              fromCounty: fromAssignment.toAddress.county,
              toName: toAssignment.fromAddress.name,
              toAddress: toAssignment.fromAddress.address,
              toCity: toAssignment.fromAddress.city,
              toZip: toAssignment.fromAddress.zip,
              toCounty: toAssignment.fromAddress.county,
              breakTime: 0,
            } as AssignmentModel,
          ],
        } as CaseModel,
      } as CaseExtendedModel;
    }

    if (defaults) {
      modalStack.push(
        <ContextWrappedCreateCaseModal
          goToCreated={false}
          onClose={() => {
            modalStack.pop();
          }}
          onCaseCreated={() => {
            modalStack.pop();
          }}
          newAssignmentOverrides={{
            firstPossibleStartTime: placeholderStartTime,
            lastPossibleEndTime: placeholderEndTime,
          }}
          defaults={defaults}
          caseType={defaults.case.caseTypeID}
        />
      );
    }
  };

  return {
    handleCreateAfternoonPlaceholder,
    handleCreateMorningPlaceholder,
    handleMidAssignmentPlaceholder,
  };
}
